import React, { useEffect, useState } from "react";
import { Page, setPage } from "features/appstate/AppStateSlice";
import { RootState } from "store/store";
import { connect } from "react-redux";
import { RouteComponentProps, useLocation } from "react-router-dom";
import { Row, Spin, Col, Button, Divider } from "antd";
import { Formik } from "formik";
import { Form, Switch } from "formik-antd";
import i18n from "i18n";
import qs from "qs";
import {
  Client,
  ClientNextEngineLinkSimple,
  CustomLogoUpdateForm,
} from "api/sms-api";
import {
  getClientNextEngineLinks,
  deleteClientNextEngineLink,
  createClientNextEngineShippedOrders,
} from "./EcSettingsActions";
import {
  updateCustomLogoSetting,
  getCustomLogoSetting,
} from "features/client_custom_logo_settings/ClientCustomLogoSettingsActions";
import { surveyTypeEc } from "../../constants";
import NextEngineLinkModal from "components/NextEngineLinkModal";
import SaveButton from "components/SaveButton";

interface EcSettingsProps {
  clientNextEngineLinks: Array<ClientNextEngineLinkSimple>;
  clientNextEngineLinksLoading: boolean;
  clientNextEngineLinksDeleting: boolean;
  clientNextEngineShippedOrdersCreating: boolean;
  customLogoItem?: Client;
  customLogoLoading: boolean;
  customLogoUpdateLoading: boolean;
  getClientNextEngineLinks: () => void;
  deleteClientNextEngineLink: (clientNextEngineLinkId: string) => void;
  createClientNextEngineShippedOrders: () => void;
  updateCustomLogoSetting: (
    params: CustomLogoUpdateForm,
    file: File | null
  ) => void;
  getCustomLogoSetting: () => void;
  setPage: (page: Page) => void;
}

const EcSettings: React.FC<EcSettingsProps & RouteComponentProps> = (props) => {
  useEffect(() => {
    props.setPage(Page.ecSettings);
  }, [props.setPage]);

  useEffect(() => {
    props.getCustomLogoSetting();
  }, [props.getCustomLogoSetting]);

  const location = useLocation();
  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
  const linkFinished = parsed.linkFinished ? true : false;
  const [isNextEngineModalVisible, setIsNextEngineModalVisible] = useState(
    linkFinished
  );

  React.useEffect(() => {
    if (parsed.surveyType === surveyTypeEc) {
      props.getClientNextEngineLinks();
    }
  }, [props.getClientNextEngineLinks]);

  React.useEffect(() => {
    if (linkFinished) {
      props.createClientNextEngineShippedOrders();
    }
  }, [props.createClientNextEngineShippedOrders, linkFinished]);

  if (
    props.clientNextEngineLinksLoading ||
    props.clientNextEngineShippedOrdersCreating ||
    props.customLogoLoading ||
    props.customLogoUpdateLoading
  ) {
    return <Spin />;
  }

  const showNextEngineModal = () => {
    setIsNextEngineModalVisible(true);
  };

  const hideNextEngineModel = () => {
    setIsNextEngineModalVisible(false);
  };

  const unlinkNextEngine = (clientNextEngineLinkId: string) => {
    props.deleteClientNextEngineLink(clientNextEngineLinkId);
  };

  const handleSubmit = (form: CustomLogoUpdateForm) => {
    props.updateCustomLogoSetting(form, null);
  };

  const initialValues: CustomLogoUpdateForm = {
    hideLogo: props.customLogoItem?.hideLogo,
    customLogoUrl: props.customLogoItem?.customLogoUrl,
    hideLowerAnswerImage: props.customLogoItem?.hideLowerAnswerImage,
  };

  let clientNextEngineLinkButtonDisplay: any =
    props.clientNextEngineLinks && props.clientNextEngineLinks.length > 0 ? (
      <Button
        type="link"
        onClick={() => unlinkNextEngine(props.clientNextEngineLinks[0].id)}
      >
        {`(${i18n.t("ecSettings.link.unlink")})`}
      </Button>
    ) : (
      <Button type="link" onClick={showNextEngineModal}>
        {`(${i18n.t("ecSettings.link.link")})`}
      </Button>
    );
  // Show the spinner if the client links are still loading or being deleted
  if (
    props.clientNextEngineLinksLoading ||
    props.clientNextEngineLinksDeleting
  ) {
    clientNextEngineLinkButtonDisplay = <Spin />;
  }

  return (
    <div>
      <div className="table-button-row">
        <Row>
          <Col span={18}>
            {i18n.t("ecSettings.link.nextEngineLinkTitle")}
            {clientNextEngineLinkButtonDisplay}
            <NextEngineLinkModal
              visible={isNextEngineModalVisible}
              showFooter={true}
              isFinished={linkFinished}
              onCloseModal={hideNextEngineModel}
            />
          </Col>
        </Row>
      </div>
      <Divider />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form layout="vertical">
            <Row justify="start">
              <Col span={18}>
                <Form.Item
                  name="hideLowerAnswerImage"
                  label={i18n.t("client.hideLowerAnswerImage")}
                >
                  <Switch name="hideLowerAnswerImage" />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end" style={{ marginTop: "2.5em" }}>
              <SaveButton />
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    clientNextEngineLinks: state.ecSettings.clientNextEngineLinks,
    clientNextEngineLinksLoading: state.ecSettings.clientNextEngineLinksLoading,
    clientNextEngineLinksDeleting:
      state.ecSettings.clientNextEngineLinksDeleting,
    clientNextEngineShippedOrdersCreating:
      state.ecSettings.clientNextEngineShippedOrdersCreating,
    customLogoItem: state.clientCustomLogoSettings.item,
    customLogoLoading: state.clientCustomLogoSettings.getLoading,
    customLogoUpdateLoading: state.clientCustomLogoSettings.updateLoading,
  };
};

const mapDispatchToProps = {
  setPage,
  getClientNextEngineLinks,
  deleteClientNextEngineLink,
  createClientNextEngineShippedOrders,
  updateCustomLogoSetting,
  getCustomLogoSetting,
};

export default connect(mapStateToProps, mapDispatchToProps)(EcSettings);
